import { useLazyQuery } from '@apollo/client';
import { Box, Collapse } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { ChevronDown } from '@styled-icons/bootstrap/ChevronDown';
import { ChevronUp } from '@styled-icons/bootstrap/ChevronUp';
import { Download } from '@styled-icons/bootstrap/Download';
import DropDown from 'Components/Forms/DropDown';
import Loader from 'Components/Forms/Loader';
import { ThemeButton, ThemeTypography } from 'DesignSystem';
import useEditorState from 'FeatureModules/ProductPage/hooks/useEditorState';
import { useProduct } from 'FeatureModules/ProductPage/hooks/useProduct';
import {
  EContentBlockType,
  ImpactFact,
  IProduct,
  IProductVersion,
  IMoreProductItem,
  TContentBlockType,
} from 'FeatureModules/ProductPage/types/product.types';
import { GET_LATEST_VERSION_AND_PRODUCT, GET_PRODUCTS } from 'graphql/secure/queries';
import useDialog from 'hooks/useDialog';
import useMessages from 'hooks/useMessages';
import React, { useMemo, useState } from 'react';
import { IGraphQlConnection } from 'types/core.types';
import { v4 as uuid } from 'uuid';

const Container = styled('div')(({ theme }) => ({
  background: theme.custom.themeColors.primary[5],
  borderRadius: 6,
  padding: '8px 16px',
  cursor: 'pointer',

  '& .MuiFormControl-root': {
    width: '100%',
  },

  '& .MuiSelect-select': {
    background: '#fff !important',
    width: '100%',
    borderRadius: 4,
    border: `1px solid #EEEEEE`,
    padding: '4px 8px !important',
  },
}));

const HeadlineContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
}));

const ChevronContainer = styled('div')(({ theme }) => ({
  color: theme.custom.themeColors.grayScale[40],
}));

const ImportButton = styled(ThemeButton)(({ theme }) => ({
  marginTop: 8,
  marginBottom: 8,
  width: '100%',
}));

interface IDropDownType {
  id: string;
  title: string;
}

interface IProps {
  contentType: TContentBlockType;
  setContent: (data: unknown) => void;
}

const ImportContentBlock: React.FC<IProps> = ({ contentType, setContent }: IProps) => {
  const [getAllProducts, { data, loading }] = useLazyQuery<{
    products: IGraphQlConnection<IProduct>;
  }>(GET_PRODUCTS);
  const [selectedProduct, setSelectedProduct] = useState<IDropDownType | null>(null);
  const [openCollapse, setOpenCollapse] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const { custom } = useTheme();
  const { openDialog, closeDialog } = useDialog();
  const { setErrorMessage, setSuccessMessage } = useMessages();
  const { lang } = useEditorState();
  const { currentProductId } = useProduct();

  const [getVersion, { loading: loadingVersion }] = useLazyQuery<{
    latestProductVersion: IProductVersion;
  }>(GET_LATEST_VERSION_AND_PRODUCT, {
    // no-cache, otherwise it overrides the product journey of the current product
    fetchPolicy: 'no-cache',
  });

  const handleOpenCollapse = async () => {
    if (!openCollapse) {
      await getAllProducts();
    }
    setOpenCollapse(!openCollapse);
  };

  const dropdownOptions = useMemo(
    () =>
      data?.products?.edges
        .filter(
          // @ts-ignore
          ({ node }) => node.id !== currentProductId && node.languageConfig.enabled.includes(lang)
        )
        .map(({ node }) => ({
          id: node.id,
          title: node.title,
        })),
    [data, currentProductId, lang]
  );

  const importContentBlock = async () => {
    setHasError(false);
    if (selectedProduct) {
      const { data: { latestProductVersion = {} as IProductVersion } = {} } = await getVersion({
        variables: {
          id: selectedProduct.id,
          lang: lang,
        },
      });
      const contentBlockContent = latestProductVersion.contentBlocks?.find(
        contentBlock => contentType === contentBlock.contentType
      );

      if (!contentBlockContent) {
        setHasError(true);
        setErrorMessage('There was an error importing content from other product');
      } else if (contentBlockContent.contentType === EContentBlockType.IMPACT_FACTS) {
        const { id, ...values } = contentBlockContent.data;
        setContent({
          ...values,
          impactFacts: values.impactFacts?.map((impactFact: ImpactFact) => ({
            ...impactFact,
            id: uuid(),
          })),
        });
      } else if (contentBlockContent.contentType === EContentBlockType.MORE_PRODUCTS) {
        const { id, ...values } = contentBlockContent.data;
        setContent({
          ...values,
          moreProducts: values.moreProducts.map((product: IMoreProductItem) => ({
            ...product,
            id: uuid(),
          })),
        });
      } else {
        const { id, ...values } = contentBlockContent.data;
        setContent(values);
        setSuccessMessage('Content was successfully imported from other product');
      }
    }
  };

  const handleImportContentBlock = () => {
    openDialog({
      type: 'ALERT',
      props: {
        onSubmit: importContentBlock,
        onCancel: closeDialog,
        title: 'Import Content',
        text: 'Information may already have been entered for this content block. Do you want to overwrite the existing content with the imported content? This cannot be undone.',
        submitText: 'Overwrite',
      },
    });
  };

  if (loading) return <Loader />;

  return (
    <Container>
      <HeadlineContainer onClick={handleOpenCollapse} data-testid="import-content-button">
        <Download size={14} color={custom.themeColors.grayScale[100]} />
        <Box ml={1} mr={1}>
          <ThemeTypography variant="BODY_MEDIUM_BOLD">Import Content</ThemeTypography>
        </Box>
        <ChevronContainer>
          {openCollapse ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </ChevronContainer>
      </HeadlineContainer>
      <Collapse in={openCollapse}>
        {openCollapse && dropdownOptions && (
          <Box mt={'12px'} width={175}>
            <ThemeTypography variant="BODY_SMALL">Product to import from</ThemeTypography>
            <Box mt={'8px'}>
              <DropDown<IDropDownType>
                options={dropdownOptions}
                onChange={item => setSelectedProduct(item)}
                renderItems={item => (
                  <ThemeTypography variant={'BODY_MEDIUM'}>{item.title}</ThemeTypography>
                )}
                currentValue={selectedProduct}
                placeholder="Select product"
                data-testid="import-content-dropdown"
              />
            </Box>
            <ImportButton
              disabled={!selectedProduct}
              onClick={handleImportContentBlock}
              color="WHITE"
              loading={loadingVersion}
              data-testid="submit-import-content-btn"
            >
              Import
            </ImportButton>
            {hasError && (
              <ThemeTypography variant={'BODY_MEDIUM'} color={'RED'}>
                No content to import from this product
              </ThemeTypography>
            )}
          </Box>
        )}
      </Collapse>
    </Container>
  );
};

export default ImportContentBlock;
